import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import PreSalesTab from '@/components/PreSalesTab';
import { format } from 'date-fns';
import { ClickToCall, PhoneFieldWithCall } from '@/components/ClickToCall';
import { SocialTimeline, LinkedInActions, EmailTrackingBadge } from '@/components/SocialTimeline';
import { Phone, Linkedin } from 'lucide-react';
import HistoryTab from '@/components/HistoryTab';

const INDUSTRIES = ['E-commerce', 'Technology', 'Retail', 'Healthcare', 'Finance', 'Manufacturing', 'Education', 'Real Estate', 'Consulting', 'Media & Entertainment', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
const EMPLOYEE_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const SOURCES = ['Website', 'Contact Form', 'Get a Demo', 'Get a Call Back', 'LinkedIn', 'Referral', 'Cold Call', 'Email Campaign', 'Conference', 'Social Media', 'Self', 'Marketing', 'Internal Ref', 'Client Reference', 'Existing Client', 'Churned Client', 'Partner- India', 'Partner- Intl', 'OEM-VISPL', 'OEM-OXT', 'Other'];
const TAG_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280'];

function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showCreateOppModal, setShowCreateOppModal] = useState(false);
  const [leadOpportunities, setLeadOpportunities] = useState([]);
  const [delegatableUsers, setDelegatableUsers] = useState([]);
  const [delegateForm, setDelegateForm] = useState({ delegate_to_user_id: '', reason: '' });
  const [delegating, setDelegating] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsTotalValue, setProductsTotalValue] = useState(0);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'call',
    notes: '',
    meeting_date: '',
    meeting_time: '',
    meeting_duration: 30,
    send_email_invite: false,
    add_to_calendar: false,
    additional_attendees: '',  // Comma-separated email addresses
  });
  const [approvalForm, setApprovalForm] = useState({
    approval_type: 'qualification',
    notes: '',
  });
  const [newTag, setNewTag] = useState('');
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchLead = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}`);
      setLead(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      setMessage({ type: 'error', text: 'Lead not found' });
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/activities/lead/${leadId}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [leadId]);

  const fetchTimeline = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/timeline`);
      setTimeline(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  }, [leadId]);

  const fetchApprovalHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/approval-history`);
      setApprovalHistory(response.data);
    } catch (error) {
      console.error('Error fetching approval history:', error);
    }
  }, [leadId]);

  const fetchAssignmentHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/assignment-history`);
      setAssignmentHistory(response.data);
    } catch (error) {
      console.error('Error fetching assignment history:', error);
    }
  }, [leadId]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  const fetchDelegatableUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/delegatable-users`);
      setDelegatableUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching delegatable users:', error);
    }
  }, []);

  const fetchLinkedProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/products`);
      setLinkedProducts(response.data.products || []);
      setProductsTotalValue(response.data.total_value || 0);
    } catch (error) {
      console.error('Error fetching linked products:', error);
    }
  }, [leadId]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // New schema uses `active` (was `is_active` on legacy records). Accept either.
      setAllProducts((response.data || []).filter(p => p.active !== false && p.is_active !== false));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  const fetchLeadOpportunities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/opportunities/lead/${leadId}/opportunities`);
      setLeadOpportunities(response.data || []);
    } catch (error) {
      console.error('Error fetching lead opportunities:', error);
    }
  }, [leadId]);

  useEffect(() => {
    fetchLead();
    fetchActivities();
    fetchTimeline();
    fetchApprovalHistory();
    fetchAssignmentHistory();
    fetchTags();
    fetchLinkedProducts();
    fetchAllProducts();
    fetchLeadOpportunities();
  }, [fetchLead, fetchActivities, fetchTimeline, fetchApprovalHistory, fetchAssignmentHistory, fetchTags, fetchLinkedProducts, fetchAllProducts, fetchLeadOpportunities]);

  // Auto-enable edit mode when navigated with ?edit=true
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && lead) {
      setEditing(true);
    }
  }, [searchParams, lead]);

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/leads/${leadId}`, formData);
      setLead(formData);
      setEditing(false);
      setMessage({ type: 'success', text: 'Lead updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update lead' });
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`${API}/leads/${leadId}`);
        navigate('/leads');
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to delete lead' });
      }
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        lead_id: leadId,
        activity_type: activityForm.activity_type,
        notes: activityForm.notes,
      };

      // If it's a meeting, add meeting details
      if (activityForm.activity_type === 'meeting' && activityForm.meeting_date && activityForm.meeting_time) {
        payload.meeting_datetime = `${activityForm.meeting_date}T${activityForm.meeting_time}`;
        payload.meeting_duration = activityForm.meeting_duration;
        payload.send_email_invite = activityForm.send_email_invite;
        payload.add_to_calendar = activityForm.add_to_calendar;
        // Add additional attendees if specified
        if (activityForm.additional_attendees) {
          payload.additional_attendees = activityForm.additional_attendees
            .split(',')
            .map(e => e.trim())
            .filter(e => e);
        }
      }

      await axios.post(`${API}/activities`, payload);
      setShowActivityModal(false);
      setActivityForm({ 
        activity_type: 'call', 
        notes: '',
        meeting_date: '',
        meeting_time: '',
        meeting_duration: 30,
        send_email_invite: false,
        add_to_calendar: false,
        additional_attendees: '',
      });
      fetchActivities();
      fetchTimeline();
      fetchLead();
      
      const successMsg = activityForm.send_email_invite 
        ? 'Activity logged and meeting invite sent!' 
        : 'Activity logged successfully!';
      setMessage({ type: 'success', text: successMsg });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to log activity' });
    }
  };

  const handleRequestApproval = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads/${leadId}/request-approval`, {
        lead_id: leadId,
        ...approvalForm,
      });
      setShowApprovalModal(false);
      setApprovalForm({ approval_type: 'qualification', notes: '' });
      fetchLead();
      fetchApprovalHistory();
      fetchTimeline();
      setMessage({ type: 'success', text: 'Approval request submitted!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit approval request' });
    }
  };

  const handleDelegation = async (e) => {
    e.preventDefault();
    if (!delegateForm.delegate_to_user_id) {
      setMessage({ type: 'error', text: 'Please select a user to delegate to' });
      return;
    }
    
    setDelegating(true);
    try {
      await axios.post(`${API}/leads/delegate`, {
        lead_id: leadId,
        delegate_to_user_id: delegateForm.delegate_to_user_id,
        reason: delegateForm.reason || null,
      });
      setShowDelegateModal(false);
      setDelegateForm({ delegate_to_user_id: '', reason: '' });
      setMessage({ type: 'success', text: 'Lead delegated successfully!' });
      setTimeout(() => {
        navigate('/leads');
      }, 1500);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delegate lead' });
    } finally {
      setDelegating(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    try {
      await axios.post(`${API}/leads/${leadId}/tags`, { tags: [newTag.trim()] });
      setNewTag('');
      setShowTagModal(false);
      fetchLead();
      fetchTags();
      setMessage({ type: 'success', text: 'Tag added!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add tag' });
    }
  };

  const handleRemoveTag = async (tagName) => {
    try {
      await axios.delete(`${API}/leads/${leadId}/tags/${encodeURIComponent(tagName)}`);
      fetchLead();
      setMessage({ type: 'success', text: 'Tag removed!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove tag' });
    }
  };

  const handleAddProduct = async (productId, quantity, discountPercent) => {
    try {
      const currentProducts = lead?.linked_products || [];
      const existingIndex = currentProducts.findIndex(p => p.product_id === productId);
      
      let newProducts;
      if (existingIndex >= 0) {
        // Update existing product
        newProducts = [...currentProducts];
        newProducts[existingIndex] = {
          product_id: productId,
          quantity: quantity,
          discount_percent: discountPercent
        };
      } else {
        // Add new product
        newProducts = [...currentProducts, {
          product_id: productId,
          quantity: quantity,
          discount_percent: discountPercent
        }];
      }
      
      await axios.put(`${API}/leads/${leadId}/products`, {
        linked_products: newProducts,
        auto_calculate: true
      });
      
      setShowProductModal(false);
      fetchLead();
      fetchLinkedProducts();
      setMessage({ type: 'success', text: 'Product added to deal!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add product' });
    }
  };

  const handleRemoveProduct = async (productId) => {
    if (!window.confirm('Remove this product from the deal?')) return;
    try {
      const currentProducts = lead?.linked_products || [];
      const newProducts = currentProducts.filter(p => p.product_id !== productId);
      
      await axios.put(`${API}/leads/${leadId}/products`, {
        linked_products: newProducts,
        auto_calculate: true
      });
      
      fetchLead();
      fetchLinkedProducts();
      setMessage({ type: 'success', text: 'Product removed from deal!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove product' });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getApprovalStatusColor = (status) => {
    const colors = {
      none: 'bg-slate-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-slate-100 text-gray-800';
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'activity':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'approval_request':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'approval_action':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'assignment':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-purple-100 text-purple-800',
      qualified: 'bg-pink-100 text-pink-800',
      meeting: 'bg-orange-100 text-orange-800',
      closed_won: 'bg-green-100 text-green-800',
      closed_lost: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-slate-100 text-gray-800';
  };

  const getActivityIcon = (type) => {
    const icons = {
      call: '📞',
      email: '📧',
      meeting: '📅',
      follow_up: '🔄',
      note: '📝',
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="animate-spin w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12" data-testid="lead-not-found">
        <p className="text-slate-500 dark:text-slate-400">Lead not found</p>
        <Link to="/leads" className="text-blue-600 hover:text-indigo-700 mt-4 inline-block">
          Back to Leads
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="lead-detail-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 dark:border-slate-700 p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <Link to="/leads" className="mr-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400 dark:hover:text-slate-300" data-testid="back-to-leads-btn">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div className="h-14 w-14 rounded-xl bg-slate-900 dark:bg-slate-700 flex items-center justify-center text-white font-bold text-xl mr-4">
              {lead.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white dark:text-white" data-testid="lead-name">{lead.name}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-400">{lead.organization || 'No organization'} • {lead.industry || 'No industry'}</p>
              <div className="flex items-center space-x-2 mt-2">
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`} data-testid="lead-status">
                  {lead.status?.replace('_', ' ').toUpperCase()}
                </span>
                {lead.approval_status && lead.approval_status !== 'none' && (
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getApprovalStatusColor(lead.approval_status)}`} data-testid="approval-status">
                    {lead.approval_status === 'pending' ? '⏳ Pending Approval' : lead.approval_status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                  </span>
                )}
              </div>
              {/* Lead Owner — visible to everyone, lets admin/head trace who owns this lead */}
              <div className="flex items-center gap-3 mt-3 text-xs" data-testid="lead-owner-block">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400 dark:text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Owner:</span>
                  <span className="font-medium text-slate-700 dark:text-slate-200">{lead.owner_name || 'Unassigned'}</span>
                  {lead.owner_role && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 capitalize">{lead.owner_role.replace(/_/g, ' ')}</span>
                  )}
                </div>
                {lead.created_by_name && lead.created_by_name !== lead.owner_name && (
                  <div className="flex items-center gap-1.5 pl-3 border-l border-slate-200 dark:border-slate-700">
                    <span className="text-slate-400 dark:text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Created by:</span>
                    <span className="text-slate-600 dark:text-slate-300">{lead.created_by_name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowApprovalModal(true)}
              className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all text-sm flex items-center"
              data-testid="request-approval-btn"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Request Approval
            </button>
            <button
              onClick={() => {
                fetchDelegatableUsers();
                setShowDelegateModal(true);
              }}
              className="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all text-sm flex items-center"
              data-testid="delegate-lead-btn"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Delegate
            </button>
            {/* Click-to-Call Button */}
            {lead?.phone && lead.phone !== '-' && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  // Trigger call via ClickToCall component logic
                  const callBtn = document.querySelector('[data-testid="lead-call-action"]');
                  if (callBtn) callBtn.click();
                }}
                className="px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all text-sm flex items-center"
                data-testid="call-lead-btn"
              >
                <Phone className="w-4 h-4 mr-1" />
                Call
              </button>
            )}
            <button
              onClick={() => setShowActivityModal(true)}
              className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all text-sm flex items-center"
              data-testid="log-activity-btn"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Log Activity
            </button>
            {!editing ? (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-all text-sm"
                  data-testid="edit-lead-btn"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-all text-sm"
                  data-testid="delete-lead-btn"
                >
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleUpdate}
                  className="px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm"
                  data-testid="save-lead-btn"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(false); setFormData(lead); }}
                  className="px-3 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 text-sm"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tags Section */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 dark:border-slate-700">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 dark:text-slate-400 uppercase tracking-wide mr-2">Tags:</span>
            {(lead.tags || []).map((tag, idx) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${TAG_COLORS[idx % TAG_COLORS.length]}20`, color: TAG_COLORS[idx % TAG_COLORS.length] }}
                data-testid={`tag-${tag}`}
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 hover:text-red-600"
                  data-testid={`remove-tag-${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              onClick={() => setShowTagModal(true)}
              className="inline-flex items-center px-2 py-1 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 dark:border-slate-600 rounded-full hover:border-slate-500 dark:hover:border-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300 dark:hover:text-slate-300"
              data-testid="add-tag-btn"
            >
              + Add Tag
            </button>
          </div>
        </div>

        {/* Quick Stats - Clean KPI Style */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 text-center" data-testid="deal-value-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(lead.deal_value)}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Deal Value</p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 text-center" data-testid="lead-score-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{lead.lead_score || 0}/100</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Lead Score</p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 text-center" data-testid="win-probability-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{lead.win_probability || 0}%</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Win Probability</p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 text-center" data-testid="activities-count-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{activities.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Activities</p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 text-center" data-testid="last-contact-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">
              {lead.last_contacted ? format(new Date(lead.last_contacted), 'MMM dd') : '-'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Last Contact</p>
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div
          className={`px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
          }`}
          data-testid="message-alert"
        >
          {message.text}
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="bg-white dark:bg-slate-800 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 dark:border-slate-700">
        <div className="border-b border-slate-100 dark:border-slate-700 dark:border-slate-700">
          <nav className="flex -mb-px overflow-x-auto px-4" data-testid="tabs-navigation">
            {[
              { id: 'details', label: 'Details' },
              { id: 'opportunities', label: 'Opportunities' },
              { id: 'products', label: 'Products' },
              { id: 'presales', label: 'Pre-Sales' },
              { id: 'social', label: 'Social Timeline' },
              { id: 'timeline', label: 'Activity Log' },
              { id: 'approvals', label: 'Approvals' },
              { id: 'assignments', label: 'Assignment History' },
              { id: 'history', label: 'History' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-slate-900 dark:border-white text-slate-900 dark:text-white dark:text-white'
                    : 'border-transparent text-slate-500 dark:text-slate-400 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300 dark:hover:text-slate-200 hover:border-slate-300 dark:border-slate-600 dark:hover:border-slate-600'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6" data-testid="details-tab-content">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {/* Contact Information */}
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField
                        label="Email"
                        value={formData.email}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        type="email"
                      />
                      {/* Phone Field with Click-to-Call */}
                      {editing ? (
                        <InfoField
                          label="Phone"
                          value={formData.phone}
                          editing={editing}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          type="tel"
                        />
                      ) : (
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Phone</label>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-slate-900 dark:text-white font-mono">{formData.phone || '-'}</p>
                            {formData.phone && formData.phone !== '-' && (
                              <ClickToCall 
                                phone={formData.phone} 
                                leadId={leadId} 
                                leadName={lead?.name}
                                variant="icon"
                                size="sm"
                              />
                            )}
                          </div>
                        </div>
                      )}
                      <InfoField
                        label="LinkedIn/Website"
                        value={formData.linkedin_website}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, linkedin_website: e.target.value })}
                        type="url"
                        fullWidth
                      />
                      {/* LinkedIn Quick Actions */}
                      {!editing && formData.linkedin_website && (
                        <div className="md:col-span-2">
                          <LinkedInActions 
                            linkedinUrl={formData.linkedin_website}
                            leadId={leadId}
                            leadName={lead?.name}
                            onActivityLogged={fetchActivities}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Company Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField
                        label="Organization"
                        value={formData.organization}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      />
                      <SelectField
                        label="Industry"
                        value={formData.industry}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        options={INDUSTRIES}
                      />
                      <SelectField
                        label="Employee Size"
                        value={formData.employee_size}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, employee_size: e.target.value })}
                        options={EMPLOYEE_SIZES}
                      />
                      <SelectField
                        label="Source"
                        value={formData.source}
                        editing={editing}
                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                        options={SOURCES}
                      />
                    </div>
                  </div>

                  {/* Deal Information */}
                  <div>
                    <h3 className="section-title mb-6">Deal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label mb-2">Deal Value (₹)</label>
                        {editing ? (
                          <input
                            type="number"
                            value={formData.deal_value || ''}
                            onChange={(e) => setFormData({ ...formData, deal_value: parseFloat(e.target.value) || null })}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            data-testid="deal-value-input"
                          />
                        ) : (
                          <p className="text-slate-900 dark:text-white">{formatCurrency(formData.deal_value)}</p>
                        )}
                      </div>
                      <div>
                        <label className="form-label mb-2">Lead Score (0-100)</label>
                        {editing ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.lead_score || ''}
                            onChange={(e) => setFormData({ ...formData, lead_score: parseInt(e.target.value) || 0 })}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            data-testid="lead-score-input"
                          />
                        ) : (
                          <p className="text-slate-900 dark:text-white">{formData.lead_score || 0}/100</p>
                        )}
                      </div>
                      <div>
                        <label className="form-label mb-2">Win Probability (%)</label>
                        {editing ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.win_probability || ''}
                            onChange={(e) => setFormData({ ...formData, win_probability: parseInt(e.target.value) || 0 })}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            data-testid="win-probability-input"
                          />
                        ) : (
                          <p className="text-slate-900 dark:text-white">{formData.win_probability || 0}%</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <h3 className="section-title mb-6">Notes & Remarks</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="form-label mb-2">Remarks</label>
                        {editing ? (
                          <textarea
                            value={formData.remarks || ''}
                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                            rows="3"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            data-testid="remarks-textarea"
                          />
                        ) : (
                          <p className="text-slate-900 dark:text-white">{formData.remarks || 'No remarks'}</p>
                        )}
                      </div>
                      <div>
                        <label className="form-label mb-2">Message</label>
                        {editing ? (
                          <textarea
                            value={formData.message || ''}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            rows="3"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            data-testid="message-textarea"
                          />
                        ) : (
                          <p className="text-slate-900 dark:text-white">{formData.message || 'No message'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="section-title mb-6">Status</h3>
                    {editing ? (
                      <>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                          data-testid="status-select"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          {/* 'converted' is set automatically by the Convert flow */}
                          {lead.status === 'converted' && <option value="converted">Converted</option>}
                          {/* Closures live on the Opportunity. Admin-only options kept
                              visible so admins can still perform cleanups. */}
                          {user?.role === 'admin' && <option value="closed_won">Closed Won (admin)</option>}
                          {user?.role === 'admin' && <option value="closed_lost">Closed Lost (admin)</option>}
                        </select>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                          Leads don't close directly — qualify them and click <b>Convert to Opportunity</b>. Closures are recorded on the Opportunity.
                        </p>
                      </>
                    ) : (
                      <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                        {lead.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    )}
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.interested || false}
                          onChange={(e) => editing && setFormData({ ...formData, interested: e.target.checked })}
                          disabled={!editing}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded"
                          data-testid="interested-checkbox"
                        />
                        <label className="ml-2 block text-sm text-slate-900 dark:text-white">Interested</label>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="section-title mb-6">Quick Actions</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => { setActivityForm({ activity_type: 'call', notes: '' }); setShowActivityModal(true); }}
                        className="w-full px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all text-left flex items-center"
                        data-testid="quick-log-call-btn"
                      >
                        <span className="mr-2">📞</span> Log Call
                      </button>
                      <button
                        onClick={() => { setActivityForm({ activity_type: 'email', notes: '' }); setShowActivityModal(true); }}
                        className="w-full px-4 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all text-left flex items-center"
                        data-testid="quick-log-email-btn"
                      >
                        <span className="mr-2">📧</span> Log Email
                      </button>
                      <button
                        onClick={() => { setActivityForm({ activity_type: 'meeting', notes: '' }); setShowActivityModal(true); }}
                        className="w-full px-4 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-all text-left flex items-center"
                        data-testid="quick-log-meeting-btn"
                      >
                        <span className="mr-2">📅</span> Schedule Meeting
                      </button>
                      <button
                        onClick={() => { setActivityForm({ activity_type: 'follow_up', notes: '' }); setShowActivityModal(true); }}
                        className="w-full px-4 py-2 bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-400 rounded-lg hover:bg-pink-100 dark:hover:bg-pink-900/40 transition-all text-left flex items-center"
                        data-testid="quick-log-followup-btn"
                      >
                        <span className="mr-2">🔄</span> Log Follow-up
                      </button>
                    </div>
                  </div>

                  {/* Lead Info */}
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="section-title mb-6">Lead Info</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Created</p>
                        <p className="font-medium text-slate-900 dark:text-white">{lead.created_at ? format(new Date(lead.created_at), 'MMM dd, yyyy') : '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Last Updated</p>
                        <p className="font-medium text-slate-900 dark:text-white">{lead.updated_at ? format(new Date(lead.updated_at), 'MMM dd, yyyy') : '-'}</p>
                      </div>
                      {lead.last_contacted && (
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Last Contacted</p>
                          <p className="font-medium text-slate-900 dark:text-white">{format(new Date(lead.last_contacted), 'MMM dd, yyyy')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Opportunities Tab */}
          {activeTab === 'opportunities' && (
            <div className="space-y-4" data-testid="opportunities-tab-content">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h3 className="section-title">Opportunities</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Sales opportunities linked to this lead. An Opportunity can only be created by converting a <b>qualified</b> lead.
                  </p>
                </div>
                {(() => {
                  const status = lead.status;
                  const isQualified = status === 'qualified';
                  const isConverted = status === 'converted' || lead.has_opportunity;
                  const isClosed = ['closed_won', 'closed_lost'].includes(status);
                  if (isConverted) {
                    const linkedOpp = leadOpportunities[0];
                    if (linkedOpp) {
                      return (
                        <button
                          onClick={() => navigate(`/opportunities/${linkedOpp.id}`)}
                          className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white transition-all"
                          data-testid="go-to-opportunity-btn"
                          title="This lead has been converted. Open the linked Opportunity."
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Go to Opportunity
                        </button>
                      );
                    }
                    // Converted but no linked opp visible (e.g., deleted or out of scope).
                    return (
                      <span
                        className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-slate-200 dark:bg-slate-700 text-slate-500"
                        data-testid="converted-no-opportunity-state"
                        title="This lead is marked converted but no linked opportunity is visible to you. Ask an Admin to investigate."
                      >
                        Converted — no linked opportunity
                      </span>
                    );
                  }
                  return (
                    <button
                      onClick={() => isQualified && setShowCreateOppModal(true)}
                      disabled={!isQualified}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                        isQualified
                          ? 'btn-primary hover:from-purple-700 hover:to-indigo-700'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="convert-to-opportunity-btn"
                      title={
                        isClosed
                          ? 'This lead is closed. Closures live on the Opportunity, not the Lead.'
                          : isQualified
                          ? 'Convert this qualified lead into an Opportunity'
                          : `Move the lead to 'qualified' first to enable conversion. Current status: ${status || 'new'}.`
                      }
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Convert to Opportunity
                    </button>
                  );
                })()}
              </div>

              {/* Opportunities Summary */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Total Opportunities</p>
                    <p className="text-2xl font-bold text-purple-600">{leadOpportunities.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Open Pipeline</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ₹{leadOpportunities
                        .filter(o => !['closed_won', 'closed_lost'].includes(o.stage))
                        .reduce((sum, o) => sum + (o.deal_value || 0), 0)
                        .toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Won Value</p>
                    <p className="text-2xl font-bold text-green-600">
                      ₹{leadOpportunities
                        .filter(o => o.stage === 'closed_won')
                        .reduce((sum, o) => sum + (o.deal_value || 0), 0)
                        .toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>

              {leadOpportunities.length === 0 ? (
                <div className="text-center py-12 bg-slate-100 dark:bg-slate-700 rounded-xl">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-slate-500 dark:text-slate-400">No opportunities linked to this lead</p>
                  <p className="text-sm text-slate-400 mt-1">Create an opportunity to start tracking deals</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leadOpportunities.map((opp) => (
                    <div 
                      key={opp.id} 
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition cursor-pointer" 
                      data-testid={`opportunity-card-${opp.id}`}
                    >
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900 dark:text-white">{opp.name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                              ${opp.stage === 'closed_won' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                opp.stage === 'closed_lost' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                opp.stage === 'negotiation' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                                opp.stage === 'proposal' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' :
                                'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                              }`}
                            >
                              {opp.stage?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                          {opp.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-1">{opp.description}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 min-w-[140px]">
                          <p className="text-lg font-bold text-slate-900 dark:text-white">₹{(opp.deal_value || 0).toLocaleString('en-IN')}</p>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${opp.probability >= 70 ? 'bg-green-500' : opp.probability >= 40 ? 'bg-amber-500' : 'bg-slate-400'}`}
                                style={{ width: `${opp.probability || 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 dark:text-slate-400">{opp.probability || 0}%</span>
                          </div>
                          {opp.expected_close_date && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Close: {format(new Date(opp.expected_close_date), 'MMM dd, yyyy')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="space-y-4" data-testid="products-tab-content">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h3 className="section-title">Linked Products</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Associate products with this deal for automatic value calculation</p>
                </div>
                <button
                  onClick={() => setShowProductModal(true)}
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium"
                  data-testid="add-product-to-lead-btn"
                >
                  + Add Product
                </button>
              </div>

              {/* Products Summary Card */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800">
                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Total Products</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{linkedProducts.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Calculated Deal Value</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">₹{productsTotalValue.toLocaleString('en-IN')}</p>
                  </div>
                  {lead?.deal_value_auto && (
                    <div className="flex items-center">
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                        Auto-calculated
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {linkedProducts.length === 0 ? (
                <div className="text-center py-12 bg-slate-100 dark:bg-slate-700 rounded-xl">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p className="text-slate-500 dark:text-slate-400">No products linked to this lead</p>
                  <p className="text-sm text-slate-400 mt-1">Add products to auto-calculate deal value</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {linkedProducts.map((item, idx) => (
                    <div key={item.product_id || idx} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition" data-testid={`linked-product-${item.product_id || idx}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-slate-900 dark:text-white">{item.product_name || item.product?.name || 'Unknown product'}</h4>
                            {(item.category || item.product?.category) && (
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded text-xs">{item.category || item.product?.category}</span>
                            )}
                          </div>
                          {item.product?.sku && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">SKU: {item.product.sku}</p>
                          )}
                          {(item.product?.description) && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{item.product.description}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 min-w-[140px]">
                          <p className="text-sm text-slate-500 dark:text-slate-400">Unit Price: ₹{Number(item.base_price || item.unit_price || 0).toLocaleString('en-IN')}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">Qty: {item.quantity}</p>
                          {item.discount_percent > 0 && (
                            <p className="text-sm text-orange-600 dark:text-orange-400">Discount: {item.discount_percent}%</p>
                          )}
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">₹{Number(item.line_total || 0).toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                      <div className="flex justify-end mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button
                          onClick={() => handleRemoveProduct(item.product_id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 text-sm font-medium"
                          data-testid={`remove-product-${item.product_id || idx}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pre-Sales Tab */}
          {activeTab === 'presales' && (
            <PreSalesTab leadId={leadId} leadName={lead?.name} />
          )}

          {/* Social Timeline Tab */}
          {activeTab === 'social' && (
            <div className="space-y-4" data-testid="social-tab-content">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="section-title">Social Activity Timeline</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">All touchpoints: LinkedIn, WhatsApp, Email, Calls</p>
                </div>
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium"
                  data-testid="social-log-activity-btn"
                >
                  + Log Activity
                </button>
              </div>
              <SocialTimeline leadId={leadId} compact={false} />
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-4" data-testid="timeline-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h3 className="section-title">Complete Activity Timeline</h3>
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium"
                  data-testid="timeline-log-activity-btn"
                >
                  + Log Activity
                </button>
              </div>
              {timeline.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">No activity recorded yet</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  {timeline.map((item) => (
                    <div key={item.id || item.created_at} className="relative pl-10 pb-6" data-testid={`timeline-item-${item.id || item.created_at}`}>
                      <div className={`absolute left-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        item.type === 'activity' ? 'bg-blue-100 text-blue-600' :
                        item.type === 'approval_request' ? 'bg-yellow-100 text-yellow-600' :
                        item.type === 'approval_action' ? (item.status === 'approved' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600') :
                        'bg-purple-100 text-purple-600'
                      }`}>
                        {getTimelineIcon(item.type)}
                      </div>
                      <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white capitalize">
                              {item.type === 'activity' ? `${item.activity_type?.replace('_', ' ')} Activity` :
                               item.type === 'approval_request' ? `Approval Request (${item.approval_type})` :
                               item.type === 'approval_action' ? `Approval ${item.status}` :
                               `Assigned to ${item.to_user}`}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{item.user_name} • {item.timestamp ? format(new Date(item.timestamp), 'MMM dd, yyyy HH:mm') : '-'}</p>
                          </div>
                          {item.status && (
                            <span className={`px-2 py-1 text-xs rounded-full ${getApprovalStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          )}
                        </div>
                        {item.notes && <p className="text-slate-600 dark:text-slate-400 mt-2">{item.notes}</p>}
                        {item.from_user && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">From: {item.from_user}</p>}
                        {item.rejection_reason && <p className="text-sm text-red-600 mt-1">Reason: {item.rejection_reason}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <div className="space-y-4" data-testid="approvals-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h3 className="section-title">Approval History</h3>
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all text-sm font-medium"
                  data-testid="approvals-request-btn"
                >
                  + Request Approval
                </button>
              </div>
              {approvalHistory.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">No approval requests yet</p>
              ) : (
                <div className="space-y-4">
                  {approvalHistory.map((approval) => (
                    <div key={approval.id || approval.requested_at} className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4 border-l-4 border-l-yellow-500" data-testid={`approval-item-${approval.id || approval.requested_at}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white capitalize">{approval.approval_type?.replace('_', ' ')} Approval</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Requested by {approval.requester_name} on {approval.requested_at ? format(new Date(approval.requested_at), 'MMM dd, yyyy HH:mm') : '-'}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full ${getApprovalStatusColor(approval.status)}`}>
                          {approval.status?.toUpperCase()}
                        </span>
                      </div>
                      {approval.notes && <p className="text-slate-600 dark:text-slate-400 mt-2">{approval.notes}</p>}
                      {approval.actioned_at && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                          {approval.status === 'approved' ? 'Approved' : 'Rejected'} by {approval.approver_name} on {approval.actioned_at ? format(new Date(approval.actioned_at), 'MMM dd, yyyy HH:mm') : '-'}
                        </p>
                      )}
                      {approval.rejection_reason && (
                        <p className="text-sm text-red-600 mt-1">Rejection Reason: {approval.rejection_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Assignment History Tab */}
          {activeTab === 'assignments' && (
            <div className="space-y-4" data-testid="assignments-tab-content">
              <h3 className="section-title mb-6">Assignment History</h3>
              {assignmentHistory.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">No assignment history yet</p>
              ) : (
                <div className="space-y-4">
                  {assignmentHistory.map((assignment) => (
                    <div key={assignment.id || assignment.assigned_at} className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4 border-l-4 border-l-purple-500" data-testid={`assignment-item-${assignment.id || assignment.assigned_at}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            Assigned to <span className="text-blue-600">{assignment.to_user_name}</span>
                            {assignment.from_user_name && <span className="text-slate-500 dark:text-slate-400"> from {assignment.from_user_name}</span>}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            By {assignment.assigned_by_name} on {assignment.assigned_at ? format(new Date(assignment.assigned_at), 'MMM dd, yyyy HH:mm') : '-'}
                          </p>
                        </div>
                        <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800 capitalize">
                          {assignment.assignment_type?.replace('_', ' ')}
                        </span>
                      </div>
                      {assignment.notes && <p className="text-slate-600 dark:text-slate-400 mt-2">{assignment.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab — audit trail of edits, stage changes, reassigns */}
          {activeTab === 'history' && (
            <div data-testid="lead-history-tab-content">
              <HistoryTab collection="leads" docId={leadId} />
            </div>
          )}
        </div>
      </div>

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="activity-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Log Activity</h3>
            <form onSubmit={handleActivitySubmit} className="space-y-4">
              <div>
                <label className="form-label mb-2">Activity Type</label>
                <select
                  value={activityForm.activity_type}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  data-testid="activity-type-select"
                >
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="note">Note</option>
                </select>
              </div>

              {/* Meeting Details - Show only for meeting type */}
              {activityForm.activity_type === 'meeting' && (
                <div className="bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-slate-800 dark:text-slate-100">Schedule Meeting</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block form-label">Date *</label>
                      <input
                        type="date"
                        value={activityForm.meeting_date}
                        onChange={(e) => setActivityForm({ ...activityForm, meeting_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                        data-testid="meeting-date-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block form-label">Time *</label>
                      <input
                        type="time"
                        value={activityForm.meeting_time}
                        onChange={(e) => setActivityForm({ ...activityForm, meeting_time: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                        data-testid="meeting-time-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block form-label">Duration</label>
                    <select
                      value={activityForm.meeting_duration}
                      onChange={(e) => setActivityForm({ ...activityForm, meeting_duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                      data-testid="meeting-duration-select"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activityForm.send_email_invite}
                        onChange={(e) => setActivityForm({ ...activityForm, send_email_invite: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded"
                        data-testid="send-email-checkbox"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        Send email invitation to <strong>{lead?.email}</strong>
                      </span>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activityForm.add_to_calendar}
                        onChange={(e) => setActivityForm({ ...activityForm, add_to_calendar: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 rounded"
                        data-testid="add-calendar-checkbox"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">Add to my calendar</span>
                    </label>
                    
                    {/* Additional Attendees - only show if sending invite */}
                    {activityForm.send_email_invite && (
                      <div className="mt-3">
                        <label className="form-label text-xs text-slate-500 dark:text-slate-400">Additional Attendees (optional)</label>
                        <input
                          type="text"
                          value={activityForm.additional_attendees}
                          onChange={(e) => setActivityForm({ ...activityForm, additional_attendees: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                          placeholder="colleague@company.com, manager@company.com"
                          data-testid="additional-attendees-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Comma-separated emails to also invite to this meeting
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="form-label mb-2">Notes</label>
                <textarea
                  value={activityForm.notes}
                  onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  placeholder={activityForm.activity_type === 'meeting' ? "Meeting agenda, discussion points..." : "Add notes about this activity..."}
                  data-testid="activity-notes-textarea"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowActivityModal(false);
                    setActivityForm({ 
                      activity_type: 'call', 
                      notes: '',
                      meeting_date: '',
                      meeting_time: '',
                      meeting_duration: 30,
                      send_email_invite: false,
                      add_to_calendar: false,
                      additional_attendees: '',
                    });
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                  data-testid="activity-modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700"
                  data-testid="activity-modal-submit-btn"
                >
                  {activityForm.activity_type === 'meeting' && activityForm.send_email_invite 
                    ? 'Schedule & Send Invite' 
                    : 'Log Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Request Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="approval-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Request Approval</h3>
            <form onSubmit={handleRequestApproval} className="space-y-4">
              <div>
                <label className="form-label mb-2">Approval Type</label>
                <select
                  value={approvalForm.approval_type}
                  onChange={(e) => setApprovalForm({ ...approvalForm, approval_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  data-testid="approval-type-select"
                >
                  <option value="qualification">Lead Qualification</option>
                  <option value="deal_value">Deal Value Change</option>
                  <option value="status_change">Status Change</option>
                  <option value="discount">Discount Approval</option>
                  <option value="escalation">Escalation</option>
                </select>
              </div>
              <div>
                <label className="form-label mb-2">Notes / Justification</label>
                <textarea
                  value={approvalForm.notes}
                  onChange={(e) => setApprovalForm({ ...approvalForm, notes: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  placeholder="Explain why this approval is needed..."
                  data-testid="approval-notes-textarea"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                  data-testid="approval-modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600"
                  data-testid="approval-modal-submit-btn"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delegate Lead Modal */}
      {showDelegateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="delegate-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Delegate Lead</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
              Transfer ownership of this lead to another team member. They will receive a notification.
            </p>
            <form onSubmit={handleDelegation} className="space-y-4">
              <div>
                <label className="form-label mb-2">Delegate To</label>
                <select
                  value={delegateForm.delegate_to_user_id}
                  onChange={(e) => setDelegateForm({ ...delegateForm, delegate_to_user_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  required
                  data-testid="delegate-user-select"
                >
                  <option value="">Select a team member...</option>
                  {delegatableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} ({user.role})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label mb-2">Reason (Optional)</label>
                <textarea
                  value={delegateForm.reason}
                  onChange={(e) => setDelegateForm({ ...delegateForm, reason: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  rows={3}
                  placeholder="Why are you delegating this lead?"
                  data-testid="delegate-reason-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDelegateModal(false);
                    setDelegateForm({ delegate_to_user_id: '', reason: '' });
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                  data-testid="delegate-modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={delegating}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50"
                  data-testid="delegate-modal-submit-btn"
                >
                  {delegating ? 'Delegating...' : 'Delegate Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {/* Add Product Modal */}
      {showProductModal && (
        <ProductModal
          products={allProducts}
          linkedProducts={linkedProducts}
          onAdd={handleAddProduct}
          onClose={() => setShowProductModal(false)}
        />
      )}

      {/* Create Opportunity Modal */}
      {showCreateOppModal && (
        <CreateOpportunityModal
          lead={lead}
          onClose={() => setShowCreateOppModal(false)}
          onSuccess={() => {
            setShowCreateOppModal(false);
            fetchLeadOpportunities();
            setMessage({ type: 'success', text: 'Opportunity created successfully!' });
          }}
        />
      )}

      {showTagModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="tag-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Add Tag</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label mb-2">Tag Name</label>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  placeholder="Enter tag name..."
                  data-testid="new-tag-input"
                />
              </div>
              {tags.length > 0 && (
                <div>
                  <label className="form-label mb-2">Existing Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => setNewTag(tag.name)}
                        className="px-3 py-1 text-xs rounded-full bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900/50 dark:hover:text-indigo-300"
                        style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                        data-testid={`existing-tag-${tag.name}`}
                      >
                        {tag.name} ({tag.usage_count})
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowTagModal(false); setNewTag(''); }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                  data-testid="tag-modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700"
                  data-testid="tag-modal-submit-btn"
                >
                  Add Tag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value, editing, onChange, type = 'text', fullWidth = false }) {
  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="form-label mb-2">{label}</label>
      {editing ? (
        <input
          type={type}
          value={value || ''}
          onChange={onChange}
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          data-testid={`input-${label.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-')}`}
        />
      ) : (
        <p className="text-slate-900 dark:text-white">{value || '-'}</p>
      )}
    </div>
  );
}

function SelectField({ label, value, editing, onChange, options }) {
  return (
    <div>
      <label className="form-label mb-2">{label}</label>
      {editing ? (
        <select
          value={value || ''}
          onChange={onChange}
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          data-testid={`select-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-slate-900 dark:text-white">{value || '-'}</p>
      )}
    </div>
  );
}

function ProductModal({ products, linkedProducts, onAdd, onClose }) {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const linkedProductIds = linkedProducts.map(lp => lp.product_id);
  
  const filteredProducts = products.filter(p => 
    !linkedProductIds.includes(p.id) &&
    (p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     p.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     p.sku?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedProductData = products.find(p => p.id === selectedProduct);
  const calculatedTotal = selectedProductData 
    ? (selectedProductData.price * quantity * (1 - discountPercent / 100))
    : 0;

  const handleSubmit = () => {
    if (!selectedProduct) return;
    onAdd(selectedProduct, quantity, discountPercent);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="product-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Add Product to Deal</h3>
        
        <div className="space-y-4">
          {/* Search */}
          <div>
            <label className="form-label mb-2">Search Products</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              placeholder="Search by name, category, or SKU..."
              data-testid="product-search-input"
            />
          </div>

          {/* Product Selection */}
          <div>
            <label className="form-label mb-2">Select Product *</label>
            <div className="max-h-48 overflow-y-auto border border-slate-300 dark:border-slate-600 rounded-lg">
              {filteredProducts.length === 0 ? (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                  {products.length === 0 ? 'No products available' : 'All products already linked'}
                </div>
              ) : (
                filteredProducts.map(product => (
                  <div
                    key={product.id}
                    onClick={() => setSelectedProduct(product.id)}
                    className={`p-3 cursor-pointer border-b border-gray-100 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 ${
                      selectedProduct === product.id ? 'bg-slate-50 dark:bg-slate-900 border-l-4 border-l-indigo-600' : ''
                    }`}
                    data-testid={`product-option-${product.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{product.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {product.category && `${product.category} • `}
                          {product.sku && `SKU: ${product.sku}`}
                        </p>
                      </div>
                      <p className="font-semibold text-blue-600">₹{product.price?.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quantity & Discount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label mb-2">Quantity</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="product-quantity-input"
              />
            </div>
            <div>
              <label className="form-label mb-2">Discount (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="product-discount-input"
              />
            </div>
          </div>

          {/* Calculated Total */}
          {selectedProduct && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Line Total</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {selectedProductData?.name} × {quantity}
                    {discountPercent > 0 && ` (${discountPercent}% off)`}
                  </p>
                </div>
                <p className="text-2xl font-bold text-blue-600">₹{calculatedTotal.toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
              data-testid="product-modal-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedProduct}
              className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="product-modal-add-btn"
            >
              Add to Deal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateOpportunityModal({ lead, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: lead?.organization || lead?.name || '',
    deal_value: lead?.deal_value || lead?.estimated_value || '',
    stage: 'qualification',
    probability: 10,
    expected_close_date: '',
    priority: 'medium',
    description: '',
    next_step: '',
    next_action_date: '',
    next_action_note: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        deal_value: parseFloat(formData.deal_value) || 0,
        stage: formData.stage,
        expected_close_date: formData.expected_close_date || null,
        priority: formData.priority,
        description: formData.description || '',
        next_step: formData.next_step || '',
      };

      // Use the atomic convert endpoint (iteration 74) — this also flips
      // the lead's status to 'converted' and sets has_opportunity=true.
      const response = await axios.post(`${API}/leads/${lead.id}/convert`, payload);

      if (response.data) {
        onSuccess();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to convert lead');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const STAGE_PROBABILITIES = {
    qualification: 10,
    discovery: 20,
    meeting: 40,
    proposal: 60,
    negotiation: 80
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="create-opportunity-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Create Opportunity
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label mb-1">Opportunity Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              data-testid="opp-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label mb-1">Deal Value (₹) *</label>
              <input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData(prev => ({ ...prev, deal_value: e.target.value }))}
                required
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="opp-value-input"
              />
            </div>
            <div>
              <label className="form-label mb-1">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => {
                  const stage = e.target.value;
                  setFormData(prev => ({ 
                    ...prev, 
                    stage,
                    probability: STAGE_PROBABILITIES[stage] || prev.probability
                  }));
                }}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="opp-stage-select"
              >
                <option value="qualification">Qualification</option>
                <option value="discovery">Discovery</option>
                <option value="meeting">Meeting</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label mb-1">Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData(prev => ({ ...prev, probability: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="opp-probability-input"
              />
            </div>
            <div>
              <label className="form-label mb-1">Expected Close Date</label>
              <input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_close_date: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="opp-close-date-input"
              />
            </div>
          </div>

          <div>
            <label className="form-label mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              data-testid="opp-priority-select"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="form-label mb-1">Next Step</label>
            <input
              type="text"
              value={formData.next_step}
              onChange={(e) => setFormData(prev => ({ ...prev, next_step: e.target.value }))}
              placeholder="e.g., Schedule discovery call"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400"
            />
          </div>

          {/* NAD - Next Action Date */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Next Action Date (NAD) *</label>
                <input
                  type="date"
                  value={formData.next_action_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_action_date: e.target.value }))}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="lead-detail-opp-nad"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Planned Action</label>
                <input
                  type="text"
                  value={formData.next_action_note}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_action_note: e.target.value }))}
                  placeholder="e.g., Demo call"
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-blue-300 dark:border-blue-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="lead-detail-opp-nad-note"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 btn-primary rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
              data-testid="create-opp-submit-btn"
            >
              {saving ? 'Creating...' : 'Create Opportunity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LeadDetail;
